// components/Admin/CategoriesManager.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Plus, Trash2, Loader } from 'lucide-react'
import { supabase } from '@lib/supabaseClient'

const CategoriesManager = ({ type = 'Vente' }) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [newCategory, setNewCategory] = useState('')
  const [newColor, setNewColor] = useState('#059669')

  useEffect(() => {
    fetchCategories()
  }, [type])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('type', type)
        .order('name')

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Erreur:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      alert('Veuillez entrer un nom de catégorie')
      return
    }

    try {
      const { error } = await supabase
        .from('categories')
        .insert([{
          name: newCategory.trim(),
          type: type,
          color: newColor
        }])

      if (error) throw error

      setNewCategory('')
      setNewColor('#059669')
      fetchCategories()
      alert('Catégorie créée avec succès !')
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la création de la catégorie')
    }
  }

  const handleDeleteCategory = async (id) => {
    if (!confirm('Êtes-vous sûr ? Les actualités auront leur catégorie réinitialisée.')) {
      return
    }

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id)

      if (error) throw error
      fetchCategories()
      alert('Catégorie supprimée !')
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la suppression')
    }
  }

  return (
    <div className="w-full space-y-6">
      <h3 className="text-2xl font-bold text-white">Catégories - {type}</h3>

      {/* Formulaire ajout */}
      <div className="bg-slate-800/60 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="Nom de la catégorie"
            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
          />
          
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="w-12 h-10 rounded cursor-pointer"
            />
            <span className="text-sm text-gray-400">{newColor}</span>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddCategory}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </motion.button>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center">
            <Loader className="w-6 h-6 animate-spin text-green-500" />
          </div>
        ) : categories.length === 0 ? (
          <p className="text-gray-400 text-center py-6">Aucune catégorie</p>
        ) : (
          categories.map(cat => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between bg-slate-800/60 p-4 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-6 h-6 rounded-full border-2 border-slate-600"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-white font-medium">{cat.name}</span>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDeleteCategory(cat.id)}
                className="p-2 hover:bg-red-600/20 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </motion.button>
            </motion.div>
          ))
        )}
      </div>
    </div>
  )
}

export default CategoriesManager